import React from "react";
import { RulerInput, ScreenShell, SegmentedToggle } from "../components";
import { cmToIn, displayHeight, inToCm } from "../units";
import type { HeightUnit } from "../types";
import { T } from "../theme";

type Props = {
  step: number;
  total: number;
  heightIn: number;
  unit: HeightUnit;
  onHeightChange: (inches: number) => void;
  onUnitChange: (unit: HeightUnit) => void;
  onNext: () => void;
  onBack: () => void;
  isMobile: boolean;
};

export function HeightScreen({
  step,
  total,
  heightIn,
  unit,
  onHeightChange,
  onUnitChange,
  onNext,
  onBack,
  isMobile,
}: Props): React.ReactElement {
  const h = displayHeight(heightIn, unit);
  const valid = heightIn >= 36 && heightIn <= 96;

  return (
    <ScreenShell
      step={step}
      total={total}
      title="How tall are you?"
      onBack={onBack}
      isMobile={isMobile}
      primaryLabel="Continue"
      primaryDisabled={!valid}
      onPrimary={onNext}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
        <SegmentedToggle
          options={[
            { value: "in", label: "ft / in" },
            { value: "cm", label: "cm" },
          ]}
          value={unit}
          onChange={onUnitChange}
        />
      </div>

      {unit === "in" ? (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2.8rem", fontWeight: 700, letterSpacing: "-0.035em", ...T.tabular, lineHeight: 1, color: T.fg }}>
            {h.ft}
            <span style={{ fontSize: "1.1rem", color: T.muted, fontWeight: 700, marginLeft: 6, letterSpacing: 0 }}>ft</span>
            <span style={{ marginLeft: 14 }}>{h.inches}</span>
            <span style={{ fontSize: "1.1rem", color: T.muted, fontWeight: 700, marginLeft: 6, letterSpacing: 0 }}>in</span>
          </div>
          <div style={{ marginTop: 28 }}>
            <RulerInput
              value={heightIn}
              min={48}
              max={84}
              step={0.5}
              majorStep={6}
              unit="in"
              decimals={1}
              onChange={onHeightChange}
              accent="blue"
              hideValue
            />
          </div>
        </div>
      ) : (
        <RulerInput
          value={Math.round(inToCm(heightIn))}
          min={120}
          max={220}
          step={1}
          majorStep={10}
          unit="cm"
          onChange={(cm) => onHeightChange(+cmToIn(cm).toFixed(2))}
          accent="blue"
        />
      )}
    </ScreenShell>
  );
}
